import { z } from 'zod/v4';

import { api } from '@/lib/api';
import {
    buildPaginationSearchParams,
    paginatedListResponseSchema,
    type PaginatedListResponse
} from '@/features/organizations/services/paginated-list';

const unknownObjectSchema = z.record(z.string(), z.unknown());

export const PUBLISHING_POSTS_PAID_PLAN_LIMIT_FEATURE_KEY = 'publishing.posts.published.per_account.period' as const;

export const publishingLimitFeatureKeySchema = z.enum([PUBLISHING_POSTS_PAID_PLAN_LIMIT_FEATURE_KEY]);

export const effectiveLimitSnapshotSchema = z.object({
    featureKey: publishingLimitFeatureKeySchema,
    source: z.enum(['account_override', 'organization_override', 'billing_product', 'default']),
    subject: z.object({
        type: z.enum(['organization_social_account']),
        id: z.string()
    }),
    window: z.object({
        type: z.enum(['billing_period']),
        startsAt: z.string(),
        endsAt: z.string(),
        resetAt: z.string()
    }),
    configuredLimit: z.number().int(),
    currentUsage: z.number().int(),
    activeReservations: z.number().int(),
    remainingAllowance: z.number().int()
});

export const publishingTargetSchema = z.object({
    id: z.string(),
    provider: z.string(),
    targetType: z.string(),
    displayName: z.string(),
    handleOrUsername: z.string().nullable(),
    avatarUrl: z.string().nullable().optional(),
    status: z.string()
});

export const publishJobMediaItemSchema = z.object({
    mediaType: z.string(),
    sourceUrl: z.string().nullable().optional(),
    sourceAssetId: z.string().nullable().optional(),
    publicFetchUrl: z.string().nullable().optional(),
    altText: z.string().nullable().optional(),
    metadata: unknownObjectSchema.nullable().optional()
});

export const publishDeliverySchema = z
    .object({
        id: z.string().optional(),
        socialAccountId: z.string().optional(),
        provider: z.string().optional(),
        externalTargetId: z.string().optional(),
        status: z.string().optional(),
        displayName: z.string().nullable().optional(),
        handleOrUsername: z.string().nullable().optional()
    })
    .catchall(z.unknown());

export const createPublishJobMediaItemInputSchema = z.object({
    mediaType: z.enum(['image', 'video']),
    sourceUrl: z.url().max(2048),
    sourceAssetId: z.string().max(255).nullable().optional(),
    publicFetchUrl: z.url().max(2048).nullable().optional(),
    altText: z.string().max(5000).nullable().optional(),
    metadata: unknownObjectSchema.nullable().optional()
});

export const publishJobSchema = z.object({
    id: z.string(),
    organizationId: z.string().optional(),
    requestedByUserId: z.string().optional(),
    caption: z.string(),
    firstComment: z.string().nullable().optional(),
    requestedPublishAt: z.string().nullable().optional(),
    status: z.string(),
    workflowId: z.string().nullable().optional(),
    workflowRunId: z.string().nullable().optional(),
    failureCode: z.string().nullable().optional(),
    failureMessage: z.string().nullable().optional(),
    publishedAt: z.string().nullable().optional(),
    canceledAt: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deliveries: z.array(publishDeliverySchema),
    mediaItems: z.array(publishJobMediaItemSchema).optional().default([])
});

export const createPublishJobInputSchema = z.object({
    targets: z.array(z.string()).min(1),
    mediaItems: z.array(createPublishJobMediaItemInputSchema).optional().default([]),
    caption: z.string().max(5000).default(''),
    firstComment: z.string().max(5000).nullable().optional(),
    requestedPublishAt: z.string().datetime({ offset: true }).nullable().optional()
});

export type PublishingTarget = z.infer<typeof publishingTargetSchema>;
export type PublishingLimitFeatureKey = z.infer<typeof publishingLimitFeatureKeySchema>;
export type EffectiveLimitSnapshot = z.infer<typeof effectiveLimitSnapshotSchema>;
export type PublishJob = z.infer<typeof publishJobSchema>;
export type PublishJobMediaItem = z.infer<typeof publishJobMediaItemSchema>;
export type PublishDelivery = z.infer<typeof publishDeliverySchema>;
export type CreatePublishJobInput = z.infer<typeof createPublishJobInputSchema>;
export type CreatePublishJobMediaItemInput = z.infer<typeof createPublishJobMediaItemInputSchema>;

export async function listPublishingTargets(organizationId: string): Promise<PublishingTarget[]> {
    const response = await api.get(`/v1/organizations/${organizationId}/social-accounts`, { authenticated: true });
    return z.array(publishingTargetSchema).parse(response);
}

export async function listOrganizationPublishingLimits(args: {
    organizationId: string;
    featureKey?: PublishingLimitFeatureKey;
}): Promise<EffectiveLimitSnapshot[]> {
    const response = await api.get(`/v1/organizations/${args.organizationId}/limits`, {
        authenticated: true,
        params: {
            featureKey: args.featureKey
        }
    });

    return z.array(effectiveLimitSnapshotSchema).parse(response);
}

export async function listPublishJobs(args: {
    organizationId: string;
    page?: number;
    perPage?: number;
}): Promise<PaginatedListResponse<PublishJob>> {
    const page = args.page ?? 1;
    const perPage = args.perPage ?? 50;
    const searchParams = buildPaginationSearchParams(
        { page, pageSize: perPage },
        { pageSizeParamNames: ['perPage', 'pageSize'] }
    );
    const response = await api.get(
        `/v1/organizations/${args.organizationId}/publishing/jobs?${searchParams.toString()}`,
        {
            authenticated: true
        }
    );

    return paginatedListResponseSchema(publishJobSchema).parse(response);
}

export async function getPublishJob(args: { organizationId: string; publishJobId: string }): Promise<PublishJob> {
    const response = await api.get(`/v1/organizations/${args.organizationId}/publishing/jobs/${args.publishJobId}`, {
        authenticated: true
    });

    return publishJobSchema.parse(response);
}

export async function createPublishJob(args: {
    organizationId: string;
    input: CreatePublishJobInput;
}): Promise<PublishJob> {
    const response = await api.post(`/v1/organizations/${args.organizationId}/publishing/jobs`, args.input, {
        authenticated: true
    });

    return publishJobSchema.parse(response);
}
